import React from 'react';
import { MapPin, Phone, Mail } from 'lucide-react';

const Footer = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth'
      });
    }
  };

  return (
    <footer className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-lg p-3 inline-block mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/21d41nbn_Hitech_Logo-2.jpg" 
                alt="Hi-Tech Engineers and Consultants" 
                className="h-12 w-auto object-contain"
              />
            </div>
            <p className="text-slate-400 text-sm">
              Delivering electrical and integrated engineering solutions across India for 35 years.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li>
                <button onClick={() => scrollToSection('home')} className="text-slate-300 hover:text-blue-400 transition-colors text-sm">
                  Home
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('about')} className="text-slate-300 hover:text-blue-400 transition-colors text-sm">
                  About Us
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('services')} className="text-slate-300 hover:text-blue-400 transition-colors text-sm">
                  Services
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('projects')} className="text-slate-300 hover:text-blue-400 transition-colors text-sm">
                  Projects
                </button>
              </li>
              <li>
                <button onClick={() => scrollToSection('contact')} className="text-slate-300 hover:text-blue-400 transition-colors text-sm">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          {/* Corporate Disclosures */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Corporate Disclosures</h4>
            <ul className="space-y-2">
              <li>
                <a 
                  href="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/gxh89rmh_Form_MGT_7_Annual%20Return_2020-2021.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-sm flex items-center gap-1"
                >
                  Annual Return 2020-2021
                </a>
              </li>
              <li>
                <a 
                  href="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/hz7l4b87_Form_MGT_7_Annual%20Return_2021-2022.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-sm flex items-center gap-1"
                >
                  Annual Return 2021-2022
                </a>
              </li>
              <li>
                <a 
                  href="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/hx0zyudc_Form_MGT_7_Annual%20Return_2022-2023.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-sm flex items-center gap-1"
                >
                  Annual Return 2022-2023
                </a>
              </li>
              <li>
                <a 
                  href="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/0v7sbvia_Form_MGT_7_Annual%20Return_2023-2024.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-sm flex items-center gap-1"
                >
                  Annual Return 2023-2024
                </a>
              </li>
              <li>
                <a 
                  href="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/vp6uibx8_Form_MGT_7A_Annual%20Return_2024-2025.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-sm flex items-center gap-1"
                >
                  Annual Return 2024-2025
                </a>
              </li>
              <li className="pt-2 border-t border-slate-700">
                <a 
                  href="https://customer-assets.emergentagent.com/job_ee7a6fc9-ec67-452c-823a-7c8341086989/artifacts/rmpy6mur_WHISTLE%20BLOWER%20POLICY.pdf" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-slate-300 hover:text-blue-400 transition-colors text-sm flex items-center gap-1 font-medium"
                >
                  Whistle Blower Policy
                </a>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Our Services</h4>
            <ul className="space-y-2 text-sm text-slate-300">
              <li>Electrical Solutions</li>
              <li>Substations up to 132 KV</li>
              <li>Data Center Solutions</li>
              <li>Office Fit-Outs</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-3">
              <li>
                <p className="text-slate-400 text-xs mb-1">Corporate Identity Number</p>
                <p className="text-slate-300 text-sm font-mono">U74200DL2003PTC121121</p>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-400 text-xs mb-1">Registered Office Address</p>
                  <span className="text-slate-300 text-sm">
                    E-306, Sushant Shopping Arcade<br />
                    Sushant Lok 1, Gurgaon - 122002<br />
                    Haryana, India
                  </span>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-slate-400 text-xs mb-1">Registered Corporate Address</p>
                  <span className="text-slate-300 text-sm">
                    5&6, Flat No.-B-2, First Floor<br />
                    Ghora Mohalla, Aya Nagar, Gali No.1<br />
                    Lotus Apartment, New Delhi<br />
                    South Delhi, Delhi, India - 110047
                  </span>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <div className="text-slate-300 text-sm">
                  <div>+91 124 4527700</div>
                  <div>+91 9873139665</div>
                </div>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-slate-300 text-sm">
                  <div>hitechworld@rediffmail.com</div>
                  <div>rkubansal@gmail.com</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-8 pt-8 text-center">
          <p className="text-slate-400 text-sm">
            © 2019 Hi-Tech Engineers and Consultants (P) Limited. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;